import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import httpClient from '../../helpers/httpClient'
import { toast } from "react-toastify"
import Slider from '../../Slider'
import { PopupWindow } from '../../PopupWindow'
import { DateField } from '@mui/x-date-pickers'
import PopupDropdown from '../../PopupDropdown'
import { noteTypes } from '../../../assets/data/data'
import { formatDate, parseDateValue, serializeDate } from '../../helpers/formatDate'
import Note from '../../Note'
import { isValidDateDayJS } from '../../helpers/globalFns'
import { showNotificationPopup } from '../../NotificationPopup'
import { getNoteCreatorName } from '../../helpers/getNoteCreatorName'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

const ASSURANCE_TYPE_OPTIONS = ['ABR-verzekering', 'Verzekering BA10', 'Brand']
const EMPTY_ASSURANCE = {
  type: 'ABR-verzekering',
  broker_id: null,
  company_id: null,
  policy_number: '',
  period_start_date: null,
  period_end_date: null,
  guarantee_start_date: null,
  guarantee_end_date: null,
  notes: ''
}
const PERMIT_STATUS_OPTIONS = ['ingediend', 'beroep', 'RvVB', 'RvS', 'vergund']
const PERMIT_TYPE_OPTIONS = ['afbraak', 'bouw', 'wijzigende bouwvergunning']
const EMPTY_PERMIT = {
  name: '',
  permit_type: 'bouw',
  status: 'ingediend',
  start_date: null,
  appeal_period_value: '',
  appeal_period_unit: 'days',
  final_decision_date: null
}
const APPEAL_PERIOD_UNIT_OPTIONS = ['days', 'weeks', 'months', 'years']

const parsePermits = (permits) => {
  const normalizePermit = (permit) => ({
    ...EMPTY_PERMIT,
    ...permit,
    appeal_period_value: permit?.appeal_period_value || permit?.appeal_period_duration || '',
    appeal_period_unit: permit?.appeal_period_unit || 'days'
  })

  if (Array.isArray(permits)) return permits.map(normalizePermit)
  if (!permits) return []

  try {
    const parsed = JSON.parse(permits)
    return Array.isArray(parsed) ? parsed.map(normalizePermit) : []
  } catch (error) {
    return []
  }
}

const parseAssurances = (assurances, building) => {
  const normalizeAssurance = (assurance) => ({
    ...EMPTY_ASSURANCE,
    ...assurance,
    broker_id: assurance?.broker_id || null,
    company_id: assurance?.company_id || null
  })

  if (Array.isArray(assurances)) return assurances.map(normalizeAssurance)
  if (assurances) {
    try {
      const parsed = JSON.parse(assurances)
      return Array.isArray(parsed) ? parsed.map(normalizeAssurance) : []
    } catch (error) {
      return []
    }
  }

  if (!building?.insurance && !building?.insurance_broker_id && !building?.insurance_company_id) return []

  const legacyTypes = building?.insurance
    ? building.insurance.split(',').filter(Boolean)
    : []

  return (legacyTypes.length ? legacyTypes : ['ABR-verzekering']).map(type => normalizeAssurance({
    type,
    broker_id: building?.insurance_broker_id || null,
    company_id: building?.insurance_company_id || null,
    period_start_date: building?.insurance_start_date || null,
    period_end_date: building?.insurance_end_date || null,
    guarantee_start_date: building?.insurance_guarantee_start_date || null,
    guarantee_end_date: building?.insurance_guarantee_end_date || null,
    notes: building?.notes || ''
  }))
}

const GeneralSection = forwardRef(({ building, setBuilding, actionType, contactsList, creatingNote, setCreatingNote }, ref) => {
  const [selectedNote, setSelectedNote] = useState()
  const [notes, setNotes] = useState([])
  const [noteDetailPopupIsActive, setNoteDetailPopupIsActive] = useState(false)
  const [newNoteObj, setNewNoteObj] = useState({})
  const [editingNote, setEditingNote] = useState(null)
  const [companies, setCompanies] = useState([])
  const [expandedPermitIndexes, setExpandedPermitIndexes] = useState([])
  const [expandedAssuranceIndexes, setExpandedAssuranceIndexes] = useState([])

  const fetchNotes = async () => {
    if (!building?.id) {
      return
    }

    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/notes/get/${building.id}?type=building`)
    if (response.status == 200) {
      setNotes(response.data)
    }
  }

  const fetchCompanies = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/companies/get?type=limited-info`)
      if (response.status === 200) {
        setCompanies(response.data)
      }
    } catch (error) {
      console.error('Error fetching companies:', error)
    }
  }

  useEffect(() => {
    fetchCompanies()
  }, [])

  useEffect(() => {
    if (building?.id) {
      fetchNotes()
    }
  }, [building?.id])

  useEffect(() => {
    setExpandedPermitIndexes([])
    setExpandedAssuranceIndexes([])
  }, [building?.id])

  const handleSetCoOwned = (idNr, state) => {
    setBuilding(prevState => ({ ...prevState, "co_ownership": state }))
  }

  const handleSetReducedVatRateEligible = (idNr, state) => {
    setBuilding(prevState => ({ ...prevState, reduced_vat_rate_eligible: state }))
  }

  const handleOnChangeInput = (e) => {
    const key = e.currentTarget.getAttribute("name")
    const value = e.currentTarget.value

    if (!building) return

    const prevState = { ...building }
    prevState[key] = value

    setBuilding(prevState)
  }

  const handleDateChange = (field, value) => {
    if (!building) return
    const formatted = serializeDate(value)
    setBuilding(prevState => ({ ...prevState, [field]: formatted }))
  }

  const setPermits = (permits) => {
    setBuilding(prevState => ({ ...prevState, permits: JSON.stringify(permits) }))
  }

  const handleAddPermit = () => {
    const permits = parsePermits(building?.permits)
    setPermits([...permits, { ...EMPTY_PERMIT }])
    setExpandedPermitIndexes([permits.length])
  }

  const handleUpdatePermit = (index, field, value) => {
    const permits = parsePermits(building?.permits)
    permits[index] = { ...permits[index], [field]: value }
    setPermits(permits)
  }

  const handlePermitDateChange = (index, field, value) => {
    const permits = parsePermits(building?.permits)
    permits[index] = {
      ...permits[index],
      [field]: serializeDate(value),
      ...(field === 'final_decision_date' && serializeDate(value) ? { status: 'vergund' } : {})
    }
    setPermits(permits)
  }

  const handleRemovePermit = (index) => {
    setPermits(parsePermits(building?.permits).filter((_, permitIndex) => permitIndex !== index))
    setExpandedPermitIndexes(prevIndexes => (
      prevIndexes
        .filter(permitIndex => permitIndex !== index)
        .map(permitIndex => permitIndex > index ? permitIndex - 1 : permitIndex)
    ))
  }

  const setAssurances = (assurances) => {
    setBuilding(prevState => ({ ...prevState, assurances: JSON.stringify(assurances) }))
  }

  const handleAddAssurance = () => {
    const assurances = parseAssurances(building?.assurances, building)
    setAssurances([...assurances, { ...EMPTY_ASSURANCE }])
    setExpandedAssuranceIndexes([assurances.length])
  }

  const handleUpdateAssurance = (index, field, value) => {
    const assurances = parseAssurances(building?.assurances, building)
    assurances[index] = { ...assurances[index], [field]: value }
    setAssurances(assurances)
  }

  const handleAssuranceDateChange = (index, field, value) => {
    handleUpdateAssurance(index, field, serializeDate(value))
  }

  const handleRemoveAssurance = (index) => {
    setAssurances(parseAssurances(building?.assurances, building).filter((_, assuranceIndex) => assuranceIndex !== index))
    setExpandedAssuranceIndexes(prevIndexes => (
      prevIndexes
        .filter(assuranceIndex => assuranceIndex !== index)
        .map(assuranceIndex => assuranceIndex > index ? assuranceIndex - 1 : assuranceIndex)
    ))
  }

  const handleToggleAssurance = (index) => {
    setExpandedAssuranceIndexes(prevIndexes => (
      prevIndexes.includes(index)
        ? prevIndexes.filter(assuranceIndex => assuranceIndex !== index)
        : [...prevIndexes, index]
    ))
  }

  const handleTogglePermit = (index) => {
    setExpandedPermitIndexes(prevIndexes => (
      prevIndexes.includes(index)
        ? prevIndexes.filter(permitIndex => permitIndex !== index)
        : [...prevIndexes, index]
    ))
  }

  const formatPermitDate = (date) => {
    return formatDate(date)
  }

  const handleCompanyChange = (field, value) => {
    if (!building) return
    setBuilding(prevState => ({ ...prevState, [field]: value?.id || null }))
  }

  // Get selected company by ID for display
  const getSelectedCompany = (companyId) => {
    if (!companyId) return null
    return companies.find(c => c.id === companyId)
  }

  const permits = parsePermits(building?.permits)
  const assurances = parseAssurances(building?.assurances, building)

  const setStateNewNote = (key, value) => {
    const prevState = { ...newNoteObj }
    prevState[key] = value
    setNewNoteObj(prevState)
  }

  const handleCreateNote = () => {
    setEditingNote(null)
    setNewNoteObj({})
    setCreatingNote(true)
  }

  const handleSetStateNewNoteType = (value) => {
    setStateNewNote("noteType", value)
  }

  const handleSetStateNewNoteContact = (value) => {
    setStateNewNote("contact", value)
  }

  const handleCreateNoteSubmit = async () => {
    if (!isValidDateDayJS(newNoteObj?.noteDate)) {
      toast.error("Date not valid or is empty")
      return false
    }

    if (!newNoteObj.noteType || !newNoteObj.subject) {
      toast.error("Type and subject are required")
      return false
    }

    const data = { ...newNoteObj }
    if (editingNote?.id) {
      await toast.promise(httpClient.put(import.meta.env.VITE_API_URL + `/api/notes/update/${editingNote.id}`, data),
        {
          success: `Note has been updated!`,
          error: "Something went wrong",
          pending: `Updating note...`
        })
    } else {
      data["buildingID"] = building?.id

      await toast.promise(httpClient.post(import.meta.env.VITE_API_URL + `/api/notes/add`, data),
        {
          success: `Note has been created!`,
          error: "Something went wrong",
          pending: `Creating note...`
        })
    }

    // cleanup state
    const prevStateNewNote = { ...newNoteObj }
    Object.keys(prevStateNewNote).forEach(key => {
      prevStateNewNote[key] = ''
    })
    setNewNoteObj(prevStateNewNote)
    setEditingNote(null)

    fetchNotes()
    setNoteDetailPopupIsActive(false)
    setCreatingNote(false)
    return true
  }

  const handleEditNote = (note) => {
    setEditingNote(note)
    setNewNoteObj({
      noteDate: parseDateValue(note?.date_note),
      noteType: note?.type || '',
      contact: note?.contact_id || null,
      subject: note?.subject || '',
      content: note?.note || ''
    })
    setCreatingNote(true)
  }

  const handleDeleteNote = (note) => {
    if (!note?.id) {
      return
    }

    showNotificationPopup({
      message: 'Are you sure you want to delete this note? This action cannot be undone.',
      cancelButton: { text: 'Cancel', color: 'var(--secondairy-light-medium-gray)' },
      actionButton: { text: 'Delete', color: 'var(--dark-red)' },
      onAction: async () => {
        try {
          await toast.promise(
            httpClient.delete(import.meta.env.VITE_API_URL + `/api/notes/delete/${note.id}`),
            {
              pending: 'Deleting note...',
              success: 'Note deleted 👍',
              error: 'Delete failed 😢'
            }
          )

          if (selectedNote?.id === note.id) {
            setSelectedNote(null)
            setNoteDetailPopupIsActive(false)
          }

          fetchNotes()
        } catch (error) {
          const apiMsg = error?.response?.data?.message
          toast.error(apiMsg || 'Cannot delete note.')
        }
      }
    })
  }

  useImperativeHandle(ref, () => ({
    handleCreateNote: handleCreateNoteSubmit
  }))

  return (
    <div className='details_container'>
      {/* Column 1: Details + Construction Period */}
      <div className='column'>
        <div className='panel'>
          <div className='body__section'>
            <h2>Details</h2>
            <div className='form__section no-border no-padding'>
              <div className='form__section--content'>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="building">Name</label>
                  <input className="input_field__input_box--text" id="building_name" type="text" name="building_name" value={building?.building_name || ''} onChange={handleOnChangeInput} />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="architect">Architect</label>
                  <input className="input_field__input_box--text" id="architect" type="text" name="architect" value={building?.architect || ''} onChange={handleOnChangeInput} />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="safety_coordinator">Veiligheidscoordinator</label>
                  <input className="input_field__input_box--text" id="safety_coordinator" type="text" name="safety_coordinator" value={building?.safety_coordinator || ''} onChange={handleOnChangeInput} />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label">Verlaagd BTW-tarief</label>
                  <Slider
                    text=""
                    defaultState={Boolean(building?.reduced_vat_rate_eligible)}
                    state={Boolean(building?.reduced_vat_rate_eligible)}
                    size={1.2}
                    fontSize="0.8rem"
                    onClickFunction={handleSetReducedVatRateEligible}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='panel'>
          <div className='body__section'>
            <h2>Construction Period</h2>
            <div className='form__section no-border no-padding'>
              <div className='form__section--content'>
                <div className="input_field--combo-grid">
                  <label className="input_field__label">Start Date</label>
                  <DateField 
                    className="date-picker" 
                    format="DD-MM-YYYY" 
                    value={parseDateValue(building?.construction_start_date)}
                    onChange={(value) => handleDateChange('construction_start_date', value)} 
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label">End Date</label>
                  <DateField 
                    className="date-picker" 
                    format="DD-MM-YYYY" 
                    value={parseDateValue(building?.construction_end_date)}
                    onChange={(value) => handleDateChange('construction_end_date', value)} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Column 2: Assurances */}
      <div className='column'>
        <div className='panel'>
          <div className='body__section'>
            <h2>Assurances</h2>
            <div className='form__section no-border no-padding'>
              <div className='form__section--content'>
                <button className='more--btn' type="button" onClick={handleAddAssurance}>+</button>
                {assurances.map((assurance, index) => {
                  const isExpanded = expandedAssuranceIndexes.includes(index)
                  const company = getSelectedCompany(assurance.company_id)
                  const companyName = company?.company_name || company?.name || ''

                  return (
                    <div className={`permit-card assurance-card ${isExpanded ? 'expanded' : 'collapsed'}`} key={index}>
                      <div className="permit-card__header" onClick={() => handleToggleAssurance(index)}>
                        <div className="permit-card__body">
                          <div className="permit-card__top-row">
                            <strong>{assurance.type || `Assurance ${index + 1}`}</strong>
                            {assurance.policy_number && <span className="permit-card__status">{assurance.policy_number}</span>}
                          </div>
                          <div className="permit-card__meta">{companyName || formatPermitDate(assurance.period_start_date)}</div>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="permit-card__details">
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Type</label>
                            <PopupDropdown
                              options={ASSURANCE_TYPE_OPTIONS}
                              value={assurance.type || ''}
                              onChange={(value) => handleUpdateAssurance(index, 'type', value)}
                              title={"Select type"}
                              searchBar={false}
                            />
                          </div>
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Makelaar</label>
                            <PopupDropdown
                              options={companies}
                              value={getSelectedCompany(assurance.broker_id)}
                              onChange={(value) => handleUpdateAssurance(index, 'broker_id', value?.id || null)}
                              title={"Select broker"}
                              searchBar={true}
                            />
                          </div>
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Maatschappij</label>
                            <PopupDropdown
                              options={companies}
                              value={getSelectedCompany(assurance.company_id)}
                              onChange={(value) => handleUpdateAssurance(index, 'company_id', value?.id || null)}
                              title={"Select company"}
                              searchBar={true}
                            />
                          </div>
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Polisnummer</label>
                            <input className="input_field__input_box--text" type="text" value={assurance.policy_number || ''} onChange={(e) => handleUpdateAssurance(index, 'policy_number', e.currentTarget.value)} />
                          </div>
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Periode start</label>
                            <DateField className="date-picker" format="DD-MM-YYYY" value={parseDateValue(assurance.period_start_date)} onChange={(value) => handleAssuranceDateChange(index, 'period_start_date', value)} />
                          </div>
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Periode einde</label>
                            <DateField className="date-picker" format="DD-MM-YYYY" value={parseDateValue(assurance.period_end_date)} onChange={(value) => handleAssuranceDateChange(index, 'period_end_date', value)} />
                          </div>
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Garantieperiode start</label>
                            <DateField className="date-picker" format="DD-MM-YYYY" value={parseDateValue(assurance.guarantee_start_date)} onChange={(value) => handleAssuranceDateChange(index, 'guarantee_start_date', value)} />
                          </div>
                          <div className="input_field--combo-grid">
                            <label className="input_field__label">Garantieperiode einde</label>
                            <DateField className="date-picker" format="DD-MM-YYYY" value={parseDateValue(assurance.guarantee_end_date)} onChange={(value) => handleAssuranceDateChange(index, 'guarantee_end_date', value)} />
                          </div>
                          <div className="input_field">
                            <label className="input_field__label">Vrij veld</label>
                            <textarea className="input_field__input_box--text-area" value={assurance.notes || ''} onChange={(e) => handleUpdateAssurance(index, 'notes', e.currentTarget.value)} />
                          </div>
                          <button className='permit-card__delete' type="button" onClick={() => handleRemoveAssurance(index)}>
                            <DeleteOutlineIcon />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Column 3: Permits */}
      <div className='column'>
        <div className='panel'>
          <div className='body__section'>
            <h2>Vergunningen</h2>
            <div className='form__section no-border no-padding'>
              <div className='form__section--content'>
                <button className='more--btn' type="button" onClick={handleAddPermit}>+</button>
                {permits.map((permit, index) => {
                  const isExpanded = expandedPermitIndexes.includes(index)
                  const permitTitle = permit.name?.trim() || `Vergunning ${index + 1}`

                  return (
                  <div className={`permit-card ${isExpanded ? 'expanded' : 'collapsed'}`} key={index}>
                    <div className="permit-card__header" onClick={() => handleTogglePermit(index)}>
                      <div className="permit-card__body">
                        <div className="permit-card__top-row">
                          <strong>{permitTitle}</strong>
                          <span className="permit-card__status">{permit.status || 'ingediend'}</span>
                        </div>
                        <div className="permit-card__meta">
                          <span>{permit.permit_type || 'bouw'}</span>
                          {formatPermitDate(permit.start_date) ? <span>{formatPermitDate(permit.start_date)}</span> : null}
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="permit-card__details">
                        <div className="input_field--combo-grid">
                          <label className="input_field__label">Vergunning</label>
                          <input className="input_field__input_box--text" type="text" value={permit.name || ''} onChange={(e) => handleUpdatePermit(index, 'name', e.currentTarget.value)} />
                        </div>
                        <div className="input_field--combo-grid">
                          <label className="input_field__label">Type vergunning</label>
                          <PopupDropdown
                            options={PERMIT_TYPE_OPTIONS}
                            value={permit.permit_type || 'bouw'}
                            onChange={(value) => handleUpdatePermit(index, 'permit_type', value)}
                            title={"Select type"}
                            searchBar={false}
                          />
                        </div>
                        <div className="input_field--combo-grid">
                          <label className="input_field__label">Status</label>
                          <PopupDropdown
                            options={PERMIT_STATUS_OPTIONS}
                            value={permit.status || ''}
                            onChange={(value) => handleUpdatePermit(index, 'status', value)}
                            title={"Select status"}
                            searchBar={false}
                          />
                        </div>
                        <div className="input_field--combo-grid">
                          <label className="input_field__label">Start</label>
                          <DateField
                            className="date-picker"
                            format="DD-MM-YYYY"
                            value={parseDateValue(permit.start_date)}
                            onChange={(value) => handlePermitDateChange(index, 'start_date', value)}
                          />
                        </div>
                        <div className="input_field--combo-grid">
                          <label className="input_field__label">Duur beroepsperiode</label>
                          <div className="permit-card__duration">
                            <input
                              className="input_field__input_box--text"
                              min="0"
                              type="number"
                              value={permit.appeal_period_value || ''}
                              onChange={(e) => handleUpdatePermit(index, 'appeal_period_value', e.currentTarget.value)}
                            />
                            <PopupDropdown
                              options={APPEAL_PERIOD_UNIT_OPTIONS}
                              value={permit.appeal_period_unit || 'days'}
                              onChange={(value) => handleUpdatePermit(index, 'appeal_period_unit', value)}
                              title={"Select unit"}
                              searchBar={false}
                            />
                          </div>
                        </div>
                        <div className="input_field--combo-grid">
                          <label className="input_field__label">Uiterste beslissingsdatum</label>
                          <DateField
                            className="date-picker"
                            format="DD-MM-YYYY"
                            value={parseDateValue(permit.final_decision_date)}
                            onChange={(value) => handlePermitDateChange(index, 'final_decision_date', value)}
                          />
                        </div>
                        <button className='permit-card__delete' type="button" onClick={() => handleRemovePermit(index)}>
                          <DeleteOutlineIcon />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )})}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Column 4: Notes */}
      <div className='column'>
        {actionType != 'add' && (
          <div className='panel'>
            <div className='body__section'>
              <h2>Notes</h2>
              <div className='form__section no-border no-padding'>
                <div className='form__section--content'>
                  <PopupWindow popupID={"popup-building-notes-create"} isActive={creatingNote} setIsActive={setCreatingNote}>
                    <div className='note__container--popup'>
                      <div className='form__section--content'>
                        <div className="input_field--combo-grid">
                          <label className="input_field__label" htmlFor="note_date">Note date</label>
                          <DateField className="date-picker" format="DD-MM-YYYY" value={newNoteObj.noteDate} onChange={(value) => setStateNewNote("noteDate", value)} />
                        </div>

                        <div className="input_field--combo-grid">
                          <label className="input_field__label" htmlFor="note_type">Note type</label>
                          <PopupDropdown
                            options={Object.keys(noteTypes)}
                            value={newNoteObj.noteType}
                            onChange={handleSetStateNewNoteType}
                            title={"Select note type"}
                          />
                        </div>

                        <div className="input_field--combo-grid">
                          <label className="input_field__label" htmlFor="note_type">Contact (optional)</label>
                          <PopupDropdown
                            options={contactsList}
                            value={newNoteObj.contact}
                            onChange={handleSetStateNewNoteContact}
                            title={"Select contact"}
                            searchBar={true}
                          />
                        </div>

                        <div className="input_field--combo-grid">
                          <label className="input_field__label" htmlFor="note_subject">Subject</label>
                          <input className="input_field__input_box--text" id="note_subject" type="text" name="note_subject" value={newNoteObj.subject || ''} onChange={(e) => setStateNewNote("subject", e.currentTarget.value)} />
                        </div>

                        <div className="input_field">
                          <label className="input_field__label" htmlFor="note_content">Content</label>
                          <textarea className="input_field__input_box--text-area" id="note_content" type="text" name="note_content" value={newNoteObj.content || ''} onChange={(e) => setStateNewNote("content", e.currentTarget.value)} />
                        </div>
                      </div>
                    </div>
                  </PopupWindow>

                  <PopupWindow popupID={"popup-building-notes-detail"} isActive={noteDetailPopupIsActive} setIsActive={setNoteDetailPopupIsActive}>
                    {selectedNote != null && (
                      <div className='note__container--popup note__container--popup--detail'>
                        <div className='date_note--popup'>
                          <div className='popup_window__subtitle'>Date:</div>
                          <p>{formatDate(selectedNote.date_note)}</p>
                        </div>
                        <div className='date_note--popup'>
                          <div className='popup_window__subtitle'>Created by:</div>
                          <p>{getNoteCreatorName(selectedNote)}</p>
                        </div>
                        <div className='date_note--popup'>
                          <div className='popup_window__subtitle'>Contact:</div>
                          {selectedNote.contact_id ? (
                            <p>{selectedNote.contact_id.first_name} {selectedNote.contact_id.last_name}</p>
                          ) : selectedNote.company_id ? (
                            <p>{selectedNote.company_id.company_name} (company)</p>
                          ) : null}
                        </div>
                        <div className='date_note--popup'>
                          <div className='popup_window__subtitle'>Subject:</div>
                          <p>{selectedNote.subject}</p>
                        </div>
                        <div className='note_text--popup'>
                          <div className='popup_window__subtitle'>Note:</div>
                          <p>{selectedNote.note}</p>
                        </div>
                      </div>
                    )}
                  </PopupWindow>

                  <button className='more--btn' onClick={handleCreateNote}>+</button>
                  {notes.map((note, index) => {
                    return (
                      <Note
                        popupWindowID="popup-contact-notes"
                        key={index}
                        noteObj={note}
                        type={note.type}
                        note={note.note}
                        subject={note.subject}
                        date={note.date_note}
                        setSelectedNote={setSelectedNote}
                        setPopupActive={setNoteDetailPopupIsActive}
                        onDelete={handleDeleteNote}
                        onEdit={handleEditNote}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

GeneralSection.displayName = 'GeneralSection'

export default GeneralSection 
